import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { Typography } from "@mui/material";

import DataTable from "../DataTable.jsx";
import { useMultiTableData } from "./hooks/useMultiTableData.jsx";
import PaymentImportStatusBadge from "../PaymentImportStatusBadge.jsx";
import { colorsFoundation } from "../../theme/index.js";
import {
  applyFilterWithAccessorKeys,
  applySortingWithAccessorKeys,
} from "./utils/tableUtils.js";
import PropTypes from "prop-types";

export default function PaymentImportPaymentsTable({
  paymentImportId,
  defaultRowsPerPage,
  onRowClick = () => {},
}) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const tableKey = "payments";
  const storageKey = "payment_import_payments";

  const tablesConfig = useMemo(
    () => [
      {
        key: tableKey,
        label: "Zahlungen",
        route: `data/payment-imports/${paymentImportId}/payments`,
        pageSize: defaultRowsPerPage,
        storageKey: storageKey,
        extraParams: {},
      },
    ],
    [paymentImportId, defaultRowsPerPage]
  );

  const { tableStates, updateTableState, fetchData } = useMultiTableData(
    tablesConfig,
    enqueueSnackbar
  );

  const tableState = tableStates[tableKey] || {};

  useEffect(() => {
    fetchData(tableKey);
  }, [tableKey]);

  const columns = useMemo(
    () => [
      { accessorKey: "remitter", header: "Sender", size: 200 },
      { accessorKey: "description", header: "Beschreibung", size: 250 },
      {
        accessorKey: "iban_bic",
        accessorKeys: ["iban", "bic"],
        header: "Konto",
        size: 200,
        Cell: ({ row }) => {
          const iban = row.original.iban;
          const bic = row.original.bic;
          return (
            <Typography variant="body2">
              {iban} <br />
              <small>{bic}</small>
            </Typography>
          );
        },
      },
      {
        accessorKey: "amount",
        header: "Betrag",
        size: 100,
        Cell: ({ row, cell }) => {
          if (!row || !cell) {
            return null;
          }

          const amount = cell.getValue();
          const debitCredit = row.original.debit_credit;
          const isCredit = debitCredit === "C";
          const color = isCredit
            ? colorsFoundation.status.positive[500]
            : colorsFoundation.status.negative[500];
          const displayAmount = new Intl.NumberFormat("de-DE", {
            style: "currency",
            currency: "EUR",
          }).format(isCredit ? amount : -amount);

          return (
            <span
              style={{
                color,
                fontWeight: "bold",
                textAlign: "right",
                display: "block",
              }}
            >
              {displayAmount}
            </span>
          );
        },
      },
      {
        accessorKey: "processed_at",
        header: "Datum",
        size: 100,
        Cell: ({ cell }) => {
          if (!cell) {
            return null;
          }
          const date = new Date(cell.getValue());
          return new Intl.DateTimeFormat("de-DE", {
            dateStyle: "short",
            timeStyle: "short",
          }).format(date);
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 140,
        Cell: ({ cell }) => {
          if (!cell) {
            return null;
          }
          const status = cell.getValue();

          return <PaymentImportStatusBadge status={status} />;
        },
      },
    ],
    []
  );

  return (
    <DataTable
      columns={columns}
      data={tableState.rows || []}
      isLoading={tableState.isLoading}
      hasLoaded={tableState.hasLoaded}
      pageIndex={tableState.pageIndex}
      pageSize={tableState.pageSize}
      totalRows={tableState.totalRows}
      onChangePage={(pagination) => updateTableState(tableKey, pagination)}
      onChangeSorting={(sorting) =>
        updateTableState(tableKey, {
          sorting: applySortingWithAccessorKeys(sorting, columns),
        })
      }
      onChangeFilters={(filters) =>
        updateTableState(tableKey, {
          filters: applyFilterWithAccessorKeys(filters, columns),
        })
      }
      onChangeGlobalFilter={(searchword) =>
        updateTableState(tableKey, { searchword })
      }
      storageKey={storageKey}
      onRowClick={onRowClick}
      toolbarActions={[
        {
          variant: "primary",
          label: " Zahlungen zuordnen",
          color: "primary",
          onClick: () =>
            navigate(
              `/admin/buchungen/imports/${paymentImportId}/payment-matching`
            ),
        },
      ]}
    />
  );
}

PaymentImportPaymentsTable.propTypes = {
  paymentImportId: PropTypes.oneOfType([
    PropTypes.number.isRequired,
    PropTypes.string.isRequired,
  ]),
  defaultRowsPerPage: PropTypes.number.isRequired,
  onRowClick: PropTypes.func,
};
